import { StateRegistrationCitizenship } from 'mutation/constants/state-registration-citizenship';
import {
  OpeLegisVM,
  ForeignNaturalizationVM,
  RenunciationCitizenshipVM,
  RegistrationCitizenshipFormVM,
  BodyChangeRelationship,
} from 'mutation/view-models/registration-citizenship';
import { Article } from 'shell/models/article';
import { Authority } from 'shell/models/authority';
import { RegistrationCitizenshipCancel } from 'shell/models/mutation/registration-citizenship-cancel';
import { Document } from 'shell/models/person/document';
import { Subject } from 'shell/models/subject';
import { Citizenship } from 'shell/models/person/citizenship';
import { DocumentANSC } from 'shell/models/person/document-ANSC';
import { ActType } from 'shell/constants/act-type';
import { transformDocumentDigital } from 'shell/utils/consult-person/request-transformer/shared/transform-document-digital';

export const transformerRequestRegistrationCitizenshipCancel = (
  values: RegistrationCitizenshipFormVM
): RegistrationCitizenshipCancel => {
  let dataCitizenship: OpeLegisVM | ForeignNaturalizationVM | RenunciationCitizenshipVM | null =
    null;

  switch (values.newCitizenship?.reasonCitizenship) {
    case StateRegistrationCitizenship.OpeLegis:
      dataCitizenship = values.newCitizenship?.opeLegis || {};
      break;
    case StateRegistrationCitizenship.ForeignNaturalization:
      dataCitizenship = values.newCitizenship?.foreignNaturalization || {};
      break;
    case StateRegistrationCitizenship.RenunciationCitizenship:
      dataCitizenship = values.newCitizenship?.renunciationCitizenship || {};
      break;
    default:
      dataCitizenship = {};
      break;
  }

  let attoCittadinanza: Document | DocumentANSC | undefined = undefined;

  if ((dataCitizenship as ForeignNaturalizationVM)?.actType === ActType.AttoCartaceo) {
    attoCittadinanza = {
      comuneRegistrazione:
        (dataCitizenship as ForeignNaturalizationVM)?.actType === ActType.AttoCartaceo
          ? {
              nomeComune:
                (dataCitizenship as ForeignNaturalizationVM)?.paper?.actMunicipality?.description ||
                undefined,
              codiceIstat:
                (dataCitizenship as ForeignNaturalizationVM)?.paper?.actMunicipality?.id ||
                undefined,
              siglaProvinciaIstat:
                (dataCitizenship as ForeignNaturalizationVM)?.paper?.actMunicipality?.province ||
                undefined,
              descrizioneLocalita:
                (dataCitizenship as ForeignNaturalizationVM)?.paper?.actMunicipality?.description ||
                undefined,
            }
          : undefined,
      anno: (dataCitizenship as ForeignNaturalizationVM)?.paper?.actYear || undefined,
      parte: (dataCitizenship as ForeignNaturalizationVM)?.paper?.actPart || undefined,
      serie: (dataCitizenship as ForeignNaturalizationVM)?.paper?.actSeries || undefined,
      numeroAtto: (dataCitizenship as ForeignNaturalizationVM)?.paper?.actNumber || undefined,
      volume: (dataCitizenship as ForeignNaturalizationVM)?.paper?.actVolume || undefined,
      dataFormazioneAtto:
        (dataCitizenship as ForeignNaturalizationVM)?.paper?.actDate?.toFormat('yyyy-MM-dd') ||
        undefined,
      trascritto: (dataCitizenship as ForeignNaturalizationVM)?.paper?.transcribed ? '1' : '0',
    };
  } else if ((dataCitizenship as ForeignNaturalizationVM)?.actType === ActType.AttoDigitale) {
    attoCittadinanza = transformDocumentDigital(
      (dataCitizenship as ForeignNaturalizationVM)?.digital || undefined
    );
  }

  const isForeign = (dataCitizenship as ForeignNaturalizationVM)?.isForeign || false;

  const autorita: Authority = {
    nome: isForeign
      ? (dataCitizenship as ForeignNaturalizationVM)?.foreign?.foreignState?.value || undefined
      : (dataCitizenship as ForeignNaturalizationVM)?.italian?.municipality?.description ||
        undefined,
    descrizione: isForeign
      ? (dataCitizenship as ForeignNaturalizationVM)?.foreign?.foreignState?.value || undefined
      : (dataCitizenship as ForeignNaturalizationVM)?.italian?.municipality?.description ||
        undefined,
    provincia: isForeign
      ? (dataCitizenship as ForeignNaturalizationVM)?.foreign?.foreignState?.province || undefined
      : (dataCitizenship as ForeignNaturalizationVM)?.italian?.municipality?.province || undefined,
  };

  const tipoComuneAutorita = isForeign ? '1' : '0';

  const articolo: Article = {
    descrizioneBreve: (dataCitizenship as OpeLegisVM)?.article?.el?.descrizioneBreve || undefined,
    key: {
      subEvento: (dataCitizenship as OpeLegisVM)?.article?.id?.toString() || undefined,
    },
  };

  const changeRelationship: BodyChangeRelationship[] =
    (dataCitizenship as OpeLegisVM)?.changeRelationship?.body || [];

  const cittadinanza: Citizenship = {
    dataValidita:
      (dataCitizenship as RenunciationCitizenshipVM)?.dateLoss?.toFormat('yyyy-MM-dd') || undefined,
    codiceStato: (dataCitizenship as RenunciationCitizenshipVM)?.otherCitizenship?.id || undefined,
    noteStato: null,
    descrizioneStato:
      (dataCitizenship as RenunciationCitizenshipVM)?.otherCitizenship?.value || undefined,
  };

  const elencoSoggetti: Array<Subject> = changeRelationship?.map((item) => {
    return {
      generalita: {
        codiceFiscale: {
          codFiscale: item.fiscalCode || undefined,
        },
        idSchedaSoggettoComune: {
          idSchedaSoggettoComuneIstat: item?.idCard || undefined,
        },
        nome: item.name || undefined,
        cognome: item.surname || undefined,
        senzaCognome: item.name ? undefined : '1',
        senzaNome: item.surname ? undefined : '1',
      },
      legameSoggetto: {
        codiceLegame: item.relationship?.toString(),
      },
    };
  });

  return {
    testataRichiesta: {
      dataDecorrenza: values.editDataControl?.dateValidity?.toFormat('yyyy-MM-dd') || undefined,
    },
    generalitaPerRicerca: {
      codiceFiscale: {
        codFiscale: values.person?.fiscalCode || undefined,
      },
      nome: values.person?.name || undefined,
      cognome: values.person?.surname || undefined,
      senzaCognome: values.person?.withoutSurname ? '1' : undefined,
      senzaNome: values.person?.withoutSurname ? '1' : undefined,
      idSchedaSoggettoANPR: values.person?.idSchedaSoggettoANPR || undefined,
    },
    datiCancellazione: {
      motivoCancellazione: '6', // fixed
      dataDecorrenzaCancellazione: (dataCitizenship as OpeLegisVM)?.dateLoss?.toFormat(
        'yyyy-MM-dd'
      ),
    },
    elencoSoggetti,
    datiControllo: {
      ruoloSoggetto: values?.person?.codeLink === '1' ? '1' : '2',
    },
    dataAutorita:
      (dataCitizenship as ForeignNaturalizationVM)?.dateOath?.toFormat('yyyy-MM-dd') || undefined,
    autorita,
    tipoComuneAutorita,
    attoCittadinanzaANPR: attoCittadinanza
      ? {
          atto:
            (dataCitizenship as ForeignNaturalizationVM)?.actType === ActType.AttoCartaceo
              ? attoCittadinanza
              : undefined,
          attoANSC:
            (dataCitizenship as ForeignNaturalizationVM)?.actType === ActType.AttoDigitale
              ? attoCittadinanza
              : undefined,
        }
      : undefined,
    articolo,
    cittadinanza,
  };
};
